import React, { memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import type { DLClickEventHandlerProps } from 'tsconfig/types';
import { RenderDynamicComponent } from './RenderDynamicComponent/RenderDynamicComponent';
import type { CmsContentTypeId } from 'tsconfig/types/Contentful.types';
import get from 'lodash/get';

export type CmsContentSectionProps = {
  contentJsonList: any;
  dlClickEventHandler?: DLClickEventHandlerProps;
  renderInPageLevel?: boolean;
};

type RenderDynamicComponentsProps = {
  componentDataList: any[];
  dlClickEventHandler?: DLClickEventHandlerProps;
  renderInPageLevel?: boolean | ((contentType?: CmsContentTypeId) => boolean); // To differentiate between page level rendering and section level rendering for tracking purpose
};

const RenderDynamicComponents = (props: Readonly<RenderDynamicComponentsProps>) => {
  const { componentDataList, dlClickEventHandler, renderInPageLevel } = props;

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <React.Fragment>
      {componentDataList?.map((item, index) => (
        <RenderDynamicComponent
          contentItem={item}
          dlClickEventHandler={dlClickEventHandler}
          key={`component-${item?.sys?.id}-${index + 1}`}
          renderInPageLevel={
            renderInPageLevel instanceof Function
              ? renderInPageLevel(get(item, 'sys.contentType.sys.id'))
              : renderInPageLevel
          }
          layoutIndex={index}
        />
      ))}
    </React.Fragment>
  );
};

export const MemoizedRenderDynamicComponents = memo<RenderDynamicComponentsProps>(RenderDynamicComponents);

export const CmsContentSection = memo((props: CmsContentSectionProps) => {
  const { contentJsonList, dlClickEventHandler, renderInPageLevel } = props;
  const theme = useTheme();
  const widgetTheme = theme.widgets.CmsContentSection || {};

  const Wrapper = useMemo(
    () => styled.section`
      ${widgetTheme.wrapper}
    `,
    [],
  );

  if (!contentJsonList) {
    return null;
  }

  return (
    <Wrapper data-test="dynamic-components" className="dynamicComponents">
      <MemoizedRenderDynamicComponents
        componentDataList={contentJsonList}
        dlClickEventHandler={dlClickEventHandler}
        renderInPageLevel={renderInPageLevel}
      />
    </Wrapper>
  );
});
